import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Dropdown, Icon } from 'tea-component';
import { useIntl } from 'umi';
import SvgIcon from '@home/components/SvgIcon';

import {
  NUMERICAL_ICON_GROUPS,
  NUMERICAL_ICON_NONE,
  getNumericalIconGroupKey,
  isNumericalIconNone,
  normalizeNumericalIconValue,
} from './constants';
import './index.scss';

export interface INumericalIconSelectProps {
  value?: string;
  onChange: (icon: string) => void;
  className?: string;
}

const NumericalIconSelect: React.FC<INumericalIconSelectProps> = ({
  value,
  onChange,
  className,
}) => {
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState<boolean>(false);

  const normalizedValue = useMemo(
    () => normalizeNumericalIconValue(value),
    [value],
  );
  const isNone = isNumericalIconNone(normalizedValue);
  const selectedGroupKey = getNumericalIconGroupKey(normalizedValue);

  const noneLabel = formatMessage({ id: 'dbl_chart_condition_icon_none' });

  const handleOpen = useCallback((): void => {
    setOpen(true);
  }, []);

  const handleClose = useCallback((): void => {
    setOpen(false);
  }, []);

  const handleSelect = useCallback(
    (icon: string): void => {
      onChange(icon);
      setOpen(false);
    },
    [onChange],
  );

  const trigger = (
    <div className="dbl-numerical-icon-select__trigger">
      {isNone ? (
        <span className="dbl-numerical-icon-select__trigger-label">
          {noneLabel}
        </span>
      ) : (
        <span
          className={classNames(
            'dbl-numerical-icon-select__trigger-icon',
            selectedGroupKey &&
              `dbl-numerical-icon-select__trigger-icon--${selectedGroupKey}`,
          )}
        >
          <SvgIcon type={normalizedValue} />
        </span>
      )}
    </div>
  );

  return (
    <Dropdown
      className={classNames('dbl-numerical-icon-select', className)}
      appearance="button"
      open={open}
      button={trigger}
      clickClose={false}
      updateOnChildrenChange
      onOpen={handleOpen}
      onClose={handleClose}
    >
      <div className="dbl-numerical-icon-select__panel">
        {NUMERICAL_ICON_GROUPS.map(group => (
          <div
            key={group.key}
            className={classNames(
              'dbl-numerical-icon-select__group',
              `dbl-numerical-icon-select__group--${group.key}`,
            )}
          >
            <div className="dbl-numerical-icon-select__group-title">
              {formatMessage({ id: group.labelKey })}
            </div>
            <div className="dbl-numerical-icon-select__icons">
              {group.icons.map(iconType => (
                <button
                  key={iconType}
                  type="button"
                  className={classNames('dbl-numerical-icon-select__icon-btn', {
                    'dbl-numerical-icon-select__icon-btn--selected':
                      normalizedValue === iconType,
                  })}
                  onClick={() => handleSelect(iconType)}
                >
                  <SvgIcon type={iconType} />
                </button>
              ))}
            </div>
          </div>
        ))}
        <div className="dbl-numerical-icon-select__divider" />
        <button
          type="button"
          className={classNames('dbl-numerical-icon-select__none', {
            'dbl-numerical-icon-select__none--selected': isNone,
          })}
          onClick={() => handleSelect(NUMERICAL_ICON_NONE)}
        >
          {noneLabel}
        </button>
      </div>
    </Dropdown>
  );
};

export default NumericalIconSelect;
